import { Fieldset, Layout, Upload, UploadContainer } from '@vwfs-bronson/bronson-react';
import { useTranslation } from 'react-i18next';

import React, { ChangeEvent, FunctionComponent, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { useDispatch } from 'react-redux';
import { UploadedFile } from '../../types/files';

import { AffordabilityDocumentUploadItem } from '../AffordabilityDocumentUploadItem/AffordabilityDocumentUploadItem';
import { SelectFormField } from '../Fieldset/SelectFormField';

import { documentUploadItemValidationSchema } from '../AffordabilityDocumentUploadItem/affordabilityDocumentUploadItem.validator';
import {
  DocumentFileTypeOperatingLeaseEnum,
  DocumentFileTypePrivateLeaseEnum,
} from '../../screens/NewQuotation/AffordabilityDocumentUploadPage/AffordabilityDocumentUploadPage.model';
import { fileToBase64 } from '../../services/common/fileToBase64';

import { affordabilityDocumentUploadPageSlice } from '../../screens/NewQuotation/AffordabilityDocumentUploadPage/AffordabilityDocumentUploadPage.slice';

import { updateAffordabilityDocument } from '../../services/redux/features/api/affordabilityDocument.api';
import { FlowTypeCheckerFn, useFlowTypeChecker } from '../../services/common/miscUtils';
import { TextInputFormField } from '../Fieldset/TextInputFormField';

type DocumentTypeValue = DocumentFileTypePrivateLeaseEnum | DocumentFileTypeOperatingLeaseEnum;

const isDocumentTypeOther = (documentType?: string): boolean => {
  return (
    documentType === DocumentFileTypePrivateLeaseEnum.other || documentType === DocumentFileTypeOperatingLeaseEnum.other
  );
};

const setTimeoutForFunction = (fn: () => void | Promise<void>, delay = 1000): ReturnType<typeof setTimeout> => {
  return setTimeout(() => {
    fn();
  }, delay);
};

interface DocumentUploadProps {
  description: string;
  disabledText?: string;
  supplementaryText: string;
  uploadButtonText: string;
  isDisabled?: boolean;
  accept: Array<string>;
  fieldsValiditySetter?: (areValid: boolean) => void;
  onAddAnotherDocument: (...args: any[]) => void;
}

interface DocumentTypeDropdownProps {
  flowTypeIs: FlowTypeCheckerFn;
  documentType: string;
}

const DocumentTypeDropdown: FunctionComponent<DocumentTypeDropdownProps> = ({ flowTypeIs, documentType }) => {
  const { t } = useTranslation();

  let optionList: Array<{ label: string; value: string }> = [];

  if (flowTypeIs('privateLease')) {
    optionList = [
      { label: t('formControls:select.noSelection'), value: '' },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.bankStatement'),
        value: DocumentFileTypePrivateLeaseEnum.bankStatement,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.savingsBankAccountStatement'),
        value: DocumentFileTypePrivateLeaseEnum.savingsBankAccountStatement,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.payslip'),
        value: DocumentFileTypePrivateLeaseEnum.payslip,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.contractofEmployment'),
        value: DocumentFileTypePrivateLeaseEnum.contractofEmployment,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.sourceofFunds'),
        value: DocumentFileTypePrivateLeaseEnum.sourceofFunds,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypePrivateLease.other'),
        value: DocumentFileTypePrivateLeaseEnum.other,
      },
    ];
  }

  if (flowTypeIs('operatingLease')) {
    optionList = [
      { label: t('formControls:select.noSelection'), value: '' },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.financialAccounts'),
        value: DocumentFileTypeOperatingLeaseEnum.financialAccounts,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.financialStatementPreviousYear'),
        value: DocumentFileTypeOperatingLeaseEnum.financialStatementPreviousYear,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.bankStatement'),
        value: DocumentFileTypeOperatingLeaseEnum.bankStatement,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.managementAccounts'),
        value: DocumentFileTypeOperatingLeaseEnum.managementAccounts,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.selfEmployedDocument'),
        value: DocumentFileTypeOperatingLeaseEnum.selfEmployedDocument,
      },
      {
        label: t('affordabilityDocumentUploadPage:documentFileTypeOperatingLease.other'),
        value: DocumentFileTypeOperatingLeaseEnum.other,
      },
    ];
  }

  if (optionList.length === 0) {
    return null;
  }

  return (
    <>
      <Layout.Item default="1/2">
        <SelectFormField
          fieldName="documentType"
          labelTranslationKey="documentUploadItem:uploadItemForm.documentType.label"
          optionList={optionList}
          testId="documentType"
        />
      </Layout.Item>
      {isDocumentTypeOther(documentType) && (
        <Layout.Item default="1/2">
          <TextInputFormField
            fieldName="otherDocumentTypeDescription"
            labelTranslationKey="affordabilityDocumentUploadPage:otherDocumentType.label"
            placeholderTranslationKey="affordabilityDocumentUploadPage:otherDocumentType.placeholder"
          />
        </Layout.Item>
      )}
    </>
  );
};

export const AffordabilityDocumentUpload: FunctionComponent<DocumentUploadProps> = ({
  description,
  supplementaryText,
  uploadButtonText,
  isDisabled,
  disabledText,
  accept,
  fieldsValiditySetter,
  onAddAnotherDocument,
}) => {
  const dispatch = useDispatch();
  const flowTypeIs = useFlowTypeChecker();

  const [filesList, setFileList] = useState<Set<File>>(() => new Set<File>());
  const [validFilesList] = useState(() => new Set<File>());
  const [areAllFilesValid, setAreAllFilesValid] = useState<boolean>(false);
  const [uploadedFileIdMap, setUploadedFileIdMap] = useState<Record<string, string>>({});

  const addToFileList = (file: File) => {
    filesList.add(file);
    setFileList(new Set(filesList));
  };
  const removeFromFileList = (file: File) => {
    filesList.delete(file);
    setFileList(new Set(filesList));
  };

  const onUploadSuccess = (filename: string, uploadedFileId: string) => {
    setUploadedFileIdMap((prev: Record<string, string>) => ({ ...prev, [filename]: uploadedFileId }));
  };

  const validityCallback = (file: UploadedFile, isValid: boolean) => {
    if (isValid) {
      validFilesList.add(file);
    } else {
      validFilesList.delete(file);
    }

    setAreAllFilesValid(validFilesList.size === filesList.size);
  };

  const fileChangedHandler = (event: ChangeEvent<HTMLInputElement>, isMultiple = false): void => {
    onAddAnotherDocument();

    const target = event.target as HTMLInputElement;
    const { files }: { files: FileList | null } = target;

    let array = Array.prototype.slice.call(files);

    if (files) {
      array = isMultiple && !!filesList ? array?.concat(filesList) : array;
      array.forEach((f) => addToFileList(f));
    }
  };

  useEffect(() => {
    fieldsValiditySetter?.(areAllFilesValid);
  }, [areAllFilesValid, fieldsValiditySetter]);

  return (
    <Layout>
      <Layout.Item default="1/1">
        <Fieldset.Row>
          <Formik
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            onSubmit={() => {}}
            initialValues={{
              documentType: '',
              otherDocumentTypeDescription: '',
            }}
            validationSchema={documentUploadItemValidationSchema()}
            validateOnBlur
            validateOnMount
          >
            {({ values, setFieldValue }) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                // Clear description when documentType changes and it's not "other"
                if (!isDocumentTypeOther(values.documentType)) {
                  setFieldValue('otherDocumentTypeDescription', '');
                }
              }, [values.documentType, setFieldValue]);

              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                let isCancelled = false;

                const handleDocumentTypeChange = async () => {
                  if (isCancelled) {
                    return;
                  }

                  if (!values.documentType) {
                    return;
                  }

                  // Skip if "Other" is selected but no description provided
                  if (isDocumentTypeOther(values.documentType) && !values.otherDocumentTypeDescription) {
                    return;
                  }

                  // Patch files sequentially to avoid concurrent updates.
                  const localFiles = Array.from(filesList) as UploadedFile[];

                  await localFiles.reduce(async (previousPatch, file) => {
                    await previousPatch;

                    if (isCancelled) {
                      return;
                    }

                    const uploadedFileId = uploadedFileIdMap[file.name];

                    if (!uploadedFileId) {
                      return;
                    }

                    const b64data = await fileToBase64(file, true);

                    if (isCancelled) {
                      return;
                    }

                    const additionalMetadata = [{ key: 'documentType', value: values.documentType }];
                    if (isDocumentTypeOther(values.documentType) && values.otherDocumentTypeDescription) {
                      additionalMetadata.push({
                        key: 'otherDescription',
                        value: values.otherDocumentTypeDescription,
                      });
                    }

                    await updateAffordabilityDocument(uploadedFileId, {
                      file: {
                        name: file.name,
                        mimeType: file.type,
                        base64: b64data,
                        additionalMetadata,
                      },
                    });

                    const metadataUpdate: any = {
                      type: values.documentType as DocumentTypeValue,
                    };
                    if (isDocumentTypeOther(values.documentType) && values.otherDocumentTypeDescription) {
                      metadataUpdate.otherDescription = values.otherDocumentTypeDescription;
                    }

                    dispatch(
                      affordabilityDocumentUploadPageSlice.actions.updateFileById({
                        id: uploadedFileId,
                        updates: {
                          metadata: metadataUpdate,
                        },
                      })
                    );
                  }, Promise.resolve());
                };

                const timer = setTimeoutForFunction(handleDocumentTypeChange);

                return () => {
                  isCancelled = true;
                  clearTimeout(timer);
                };
              }, [values.documentType, values.otherDocumentTypeDescription]);

              return (
                <>
                  <Layout>
                    <DocumentTypeDropdown flowTypeIs={flowTypeIs} documentType={values.documentType} />
                  </Layout>
                </>
              );
            }}
          </Formik>
        </Fieldset.Row>
      </Layout.Item>

      <Layout.Item default="1/1">
        <UploadContainer
          uploadItemsLabel=""
          upload={
            <Upload
              buttonText={uploadButtonText}
              descriptionText={description}
              descriptionSupplementaryText={supplementaryText}
              disabledText={disabledText}
              disabled={isDisabled}
              onChange={fileChangedHandler}
              accept={accept.join(',')}
            />
          }
        >
          {Array.from(filesList).map((file: UploadedFile) => {
            return (
              <UploadContainer.Item key={`${file.name}_${file.lastModified}}}`}>
                <AffordabilityDocumentUploadItem
                  file={file}
                  onDeleteSuccess={() => removeFromFileList(file)}
                  validityCallback={(isValid: boolean) => validityCallback(file, isValid)}
                  onUploadSuccess={(uploadedFileId: string) => onUploadSuccess(file.name, uploadedFileId)}
                />
              </UploadContainer.Item>
            );
          })}
        </UploadContainer>
      </Layout.Item>
    </Layout>
  );
};
